import type { ContentInput, Violation, ScanResult, VisibilityData, SearchResult } from '../lib/types';
import type { ConvexClient } from 'convex/browser';
import { BraveClient } from '../lib/braveClient';
import { FirecrawlClient } from '../lib/firecrawlClient';
import { ScreenshotClient } from '../lib/screenshotClient';
import { OpenRouterClient } from '../lib/openRouterClient';
import { QueryGenerator } from './queryGenerator';
import { Whitelist } from './whitelist';
import { Classifier } from './classifier';
import { VisibilityScorer, DEFAULT_OSN_DOMAINS } from './visibilityScorer';
import { retryWithBackoff, categorizeError, CircuitBreaker } from '../lib/retry';
import { api } from '../../convex/_generated/api';

export interface ScanConfig {
  exaApiKey: string;
  firecrawlApiKey: string;
  screenshotApiKey: string;
  openRouterApiKey: string;
  convexClient: any; // Accept both ConvexReactClient and ConvexClient (we use only .action)
  maxResultsPerQuery?: number;
  whitelistDomains?: string[];
  onProgress?: (message: string) => void;
  onProgressWithCount?: (message: string, current: number, total: number) => void;
  // Visibility scoring configuration
  osnDomains?: string[];
  visibilityAlpha?: number;
  visibilityBeta?: number;
  onEnqueueRetry?: (engine: 'exa'|'firecrawl'|'brave', run: () => Promise<void>) => void;
}

export class PiracyScanner {
  private braveClient: BraveClient;
  private firecrawlClient: FirecrawlClient;
  private screenshotClient: ScreenshotClient;
  private openRouterClient: OpenRouterClient;
  private config: ScanConfig;
  private whitelistDomains: Set<string>;
  private visibilityScorer: VisibilityScorer;

  // Circuit breakers for API resilience
  private exaCircuitBreaker: CircuitBreaker;
  private firecrawlCircuitBreaker: CircuitBreaker;
  private llmCircuitBreaker: CircuitBreaker;
  private braveCircuitBreaker: CircuitBreaker;

  constructor(config: ScanConfig) {
    this.config = config;
    this.braveClient = new BraveClient(config.convexClient as any);
    this.firecrawlClient = new FirecrawlClient(config.firecrawlApiKey, config.convexClient);
    this.screenshotClient = new ScreenshotClient(config.screenshotApiKey, config.convexClient);
    this.openRouterClient = new OpenRouterClient(config.openRouterApiKey, config.convexClient);

    // Normalize all whitelist domains (remove www., lowercase)
    const normalizedWhitelist = (config.whitelistDomains || []).map(domain =>
      domain.toLowerCase().replace(/^www\./, '')
    );
    this.whitelistDomains = new Set(normalizedWhitelist);

    console.log(`[PiracyScanner] Loaded ${this.whitelistDomains.size} whitelisted domains:`, Array.from(this.whitelistDomains));

    // Initialize visibility scorer
    this.visibilityScorer = new VisibilityScorer({
      osnDomains: config.osnDomains || DEFAULT_OSN_DOMAINS,
      alpha: config.visibilityAlpha ?? 1.5,
      beta: config.visibilityBeta ?? 1.0,
      totalResults: 50,
    });

    // Initialize circuit breakers
    this.exaCircuitBreaker = new CircuitBreaker(5, 2, 60000);
    this.firecrawlCircuitBreaker = new CircuitBreaker(5, 2, 60000);
    this.llmCircuitBreaker = new CircuitBreaker(5, 2, 60000);
    this.braveCircuitBreaker = new CircuitBreaker(5, 2, 60000);
  }

  /**
   * Scan a single content title for piracy violations
   * Returns violations array and visibility data
   */
  async scanContent(content: ContentInput): Promise<{ violations: Violation[]; visibilityData: VisibilityData[] }> {
    try {
      this.log(`🎬 Starting scan for: ${content.title}`);

      // Load known-bad domain regex patterns from settings (optional)
      try {
        const [patternStrings, domainList, trustedDomains, negativePriors, vt, llmT, ugcMin, epMin] = await Promise.all([
          this.config.convexClient.query(api.settings.getSetting, { key: 'pirateDomainPatterns' }),
          this.config.convexClient.query(api.settings.getSetting, { key: 'pirateDomains' }),
          this.config.convexClient.query(api.settings.getSetting, { key: 'trustedNewsDomains' }),
          this.config.convexClient.query(api.feedback.getFalsePositivePriors, {}),
          this.config.convexClient.query(api.settings.getSetting, { key: 'violationThreshold' }),
          this.config.convexClient.query(api.settings.getSetting, { key: 'llmConfidenceThreshold' }),
          this.config.convexClient.query(api.settings.getSetting, { key: 'ugcMinDurationMinutes' }),
          this.config.convexClient.query(api.settings.getSetting, { key: 'episodeListMinCount' }),
        ]);

        const compiledFromDomains: string[] = Array.isArray(domainList)
          ? (domainList as string[]).map((d) => this.compileDomainToRegex(d)).filter(Boolean) as string[]
          : [];

        const asPatterns: string[] = Array.isArray(patternStrings) ? (patternStrings as string[]) : [];
        const merged = [...asPatterns, ...compiledFromDomains];

        if (merged.length > 0) {
          Classifier.setDomainPatterns(merged);
          this.log(`🛠️ Loaded ${merged.length} pirate domain patterns (domains + regex)`);
        } else {
          Classifier.setDomainPatterns(null);
        }

        // Feed simple pirate domains to the visibility scorer so SQS can attribute pirates
        if (Array.isArray(domainList) && domainList.length > 0) {
          this.visibilityScorer.updateConfig({ pirateDomains: domainList as string[] });
        }

        // Set trusted news/media domains (Phase 2: reduce false positives)
        if (Array.isArray(trustedDomains) && trustedDomains.length > 0) {
          Classifier.setTrustedDomains(trustedDomains as string[]);
          this.log(`✅ Loaded ${trustedDomains.length} trusted news/media domains`);
        }

        // Set negative priors from feedback
        if (negativePriors) {
          Classifier.setNegativePriors(negativePriors.domains, negativePriors.patterns);
        }

        // Apply classifier tunables from settings
        Classifier.setTunables({
          violationThreshold: typeof vt === 'number' ? vt : undefined,
          llmConfidenceThreshold: typeof llmT === 'number' ? llmT : undefined,
          ugcMinDurationMinutes: typeof ugcMin === 'number' ? ugcMin : undefined,
          episodeListMinCount: typeof epMin === 'number' ? epMin : undefined,
        });
      } catch (e) {
        console.warn('[PiracyScanner] Could not load pirateDomainPatterns from settings:', e);
      }

      // Step 1: Generate search queries
      this.log('📝 Generating search queries...');
      const queries = QueryGenerator.getAllQueries(content);
      this.log(`✅ Generated ${queries.length} search queries (English + Arabic)`);
      queries.slice(0, 3).forEach((q, i) => this.log(`  Query ${i+1}: "${q}"`));
      if (queries.length > 3) this.log(`  ... and ${queries.length - 3} more queries`);

      // Step 2a: Search with Exa (Method 1)
      this.log('🔍 Method 1: Searching with Exa...');
      // Unified detection sanitization: normalize quotes/spaces, keep -site filters intact
      const sanitizeDetection = (q: string) => q
        .replace(/[“”]/g, '"')
        .replace(/^\"|\"$/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      const exaQueries = queries.map(sanitizeDetection);
      this.log(`   (Exa sanitized sample): "${exaQueries[0]}"`);
      // Per-query progress
      const exaSearchResults: any[] = [];
      const exaSeen = new Set<string>();
      for (let i = 0; i < exaQueries.length; i++) {
        await this.waitIfPaused();
        const q = exaQueries[i];
        const perQuery = await retryWithBackoff<any[]>(
          () => this.exaCircuitBreaker.execute(() =>
            this.config.convexClient.action(api.exa.search, {
              query: q,
              numResults: this.config.maxResultsPerQuery ?? 10,
            })
          ),
          {
            maxAttempts: 2,
            initialDelayMs: 1000,
            onRetry: (attempt) => this.log(`⚠️ Exa retry ${attempt}/${2} for: ${q.substring(0, 48)}...`),
          }
        );
        const items: any[] = perQuery.success ? (perQuery.data || []) : [];
        if (!perQuery.success || items.length === 0) {
          if (items.length === 0) {
            this.log(`⚠️ Exa returned 0 results for query: ${q}`);
          }
          if (this.config.onEnqueueRetry) {
            const run = async () => {
              await this.config.convexClient.action(api.exa.search, { query: q, numResults: this.config.maxResultsPerQuery ?? 10 });
            };
            this.config.onEnqueueRetry('exa', run);
          }
        }
        for (const r of items) {
          const key = (r.url || '').toLowerCase().replace(/\/$/, '');
          if (!exaSeen.has(key)) { exaSeen.add(key); exaSearchResults.push(r); }
        }
        this.logWithCount('Exa search queries', i + 1, exaQueries.length);
      }
      this.log(`✅ Exa found ${exaSearchResults.length} URLs`);
      if (exaSearchResults.length === 0) {
        this.log('⚠️ Exa returned 0 URLs across all queries. Check EXA_API_KEY, quotas, and query syntax (minus-site filters may reduce matches).');
      }

      // Step 2b: Search with Firecrawl (Method 2)
      this.log('🔥 Method 2: Searching with Firecrawl (with content)...');
      // Per-query progress
      const firecrawlSearchResults: any[] = [];
      for (let i = 0; i < queries.length; i++) {
        await this.waitIfPaused();
        const q = sanitizeDetection(queries[i]);
        const batch = await retryWithBackoff<any[]>(
          () => this.firecrawlCircuitBreaker.execute(() => this.firecrawlClient.search(q, this.config.maxResultsPerQuery ?? 10)),
          { maxAttempts: 2, initialDelayMs: 1000 }
        );
        if (batch.success && batch.data) firecrawlSearchResults.push(...batch.data);
        else if (this.config.onEnqueueRetry) {
          const run = async () => {
            await this.config.convexClient.action(api.firecrawl.searchQuery, { query: q, limit: this.config.maxResultsPerQuery ?? 10 });
          };
          this.config.onEnqueueRetry('firecrawl', run);
        }
        this.logWithCount('Firecrawl search queries', i + 1, queries.length);
      }
      this.log(`✅ Firecrawl found ${firecrawlSearchResults.length} URLs with content`);

      // Step 2c: Search with Brave (Method 3)
      this.log('🦁 Method 3: Searching with Brave...');
      const isArabic = (q: string) => /[\u0600-\u06FF]/.test(q);
      // Brave API does not support Google-style -site: filters; strip them to avoid empty result sets
      const sanitizeForBraveDetect = (q: string) => sanitizeDetection(q).replace(/\s*-site:\S+/g, '').trim();
      const braveDetectQueries = queries.map(sanitizeForBraveDetect);
      const braveResultsAll: any[] = [];
      for (let i = 0; i < braveDetectQueries.length; i++) {
        await this.waitIfPaused();
        const q = braveDetectQueries[i];
        let items: any[] = [];
        if (isArabic(q)) {
          const primary = await retryWithBackoff<any[]>(
            () => this.braveCircuitBreaker.execute(() => this.config.convexClient.action(api.brave.searchQuery, { query: q, limit: this.config.maxResultsPerQuery ?? 10, lang: 'ar', country: 'SA', safesearch: 'off' })),
            { maxAttempts: 2, initialDelayMs: 1000 }
          );
          if (primary.success && primary.data) items = primary.data as any[];
          if (items.length === 0) {
            for (const c of ['ALL']) {
              const alt = await retryWithBackoff<any[]>(
                () => this.braveCircuitBreaker.execute(() => this.config.convexClient.action(api.brave.searchQuery, { query: q, limit: this.config.maxResultsPerQuery ?? 10, lang: 'ar', country: c as any, safesearch: 'off' })),
                { maxAttempts: 2, initialDelayMs: 1000 }
              );
              if (alt.success && alt.data && alt.data.length) { items = alt.data as any[]; break; }
            }
            if (items.length === 0) {
              const broad = await retryWithBackoff<any[]>(
                () => this.braveCircuitBreaker.execute(() => this.config.convexClient.action(api.brave.searchQuery, { query: q, limit: this.config.maxResultsPerQuery ?? 10, safesearch: 'off' })),
                { maxAttempts: 2, initialDelayMs: 1000 }
              );
              items = (broad.data as any[]) || [];
              if ((!primary.success || (primary.data||[]).length===0) && (!items || items.length===0) && this.config.onEnqueueRetry) {
                const run = async () => {
                  await this.config.convexClient.action(api.brave.searchQuery, { query: q, limit: this.config.maxResultsPerQuery ?? 10, lang: 'ar', country: 'SA', safesearch: 'off' });
                };
                this.config.onEnqueueRetry('brave', run);
              }
            }
          }
        } else {
          const primary = await retryWithBackoff<any[]>(
            () => this.braveCircuitBreaker.execute(() => this.config.convexClient.action(api.brave.searchQuery, { query: q, limit: this.config.maxResultsPerQuery ?? 10, lang: 'en', safesearch: 'off' })),
            { maxAttempts: 2, initialDelayMs: 1000 }
          );
          items = (primary.data as any[]) || [];
          if (items.length === 0) {
            const broad = await retryWithBackoff<any[]>(
              () => this.braveCircuitBreaker.execute(() => this.config.convexClient.action(api.brave.searchQuery, { query: q, limit: this.config.maxResultsPerQuery ?? 10, safesearch: 'off' })),
              { maxAttempts: 2, initialDelayMs: 1000 }
            );
            items = (broad.data as any[]) || [];
            if ((!primary.success || (primary.data||[]).length===0) && (!items || items.length===0) && this.config.onEnqueueRetry) {
              const run = async () => {
                await this.config.convexClient.action(api.brave.searchQuery, { query: q, limit: this.config.maxResultsPerQuery ?? 10, lang: 'en', safesearch: 'off' });
              };
              this.config.onEnqueueRetry('brave', run);
            }
          }
        }
        for (const r of items) {
          try {
            braveResultsAll.push({
              url: r.url,
              title: r.title || '',
              snippet: r.description || '',
              domain: new URL(r.url).hostname,
              rank: 0, // Placeholder rank, will be assigned globally later
            });
          } catch (e) {
            console.warn(`[Brave] Failed to parse URL: ${r.url}`, e);
          }
        }
        if (items.length === 0) {
          this.log(`⚠️ Brave returned 0 results for query: ${q}`);
        }
        this.logWithCount('Brave search queries', i + 1, braveDetectQueries.length);
      }
      const braveSearchResults = braveResultsAll;
      this.log(`✅ Brave found ${braveSearchResults.length} URLs (${braveDetectQueries.filter(isArabic).length} AR queries, ${braveDetectQueries.filter(q => !isArabic(q)).length} EN queries)`);
      if (braveSearchResults.length === 0) {
        this.log('⚠️ Brave returned 0 URLs. Note: we strip -site: filters for Brave; ensure BRAVE_API_KEY is set and valid.');
      }

      // Attribution bookkeeping (Phase 1)
      const attributionMap = new Map<string, { sources: Set<'exa' | 'firecrawl' | 'brave'>; scraped?: boolean; verified?: boolean; verificationMethod?: string }>();
      const ensureEntry = (u: string) => {
        const key = u.toLowerCase().replace(/\/$/, '');
        if (!attributionMap.has(key)) attributionMap.set(key, { sources: new Set() });
        return attributionMap.get(key)!;
      };
      const candidatesGross = { exa: new Set<string>(), firecrawl: new Set<string>(), brave: new Set<string>() };
      const candidatesNet = { exa: new Set<string>(), firecrawl: new Set<string>(), brave: new Set<string>() };
      const scrapedSuccess = { exa: 0, brave: 0 };

      // Step 2d: Combine and deduplicate results
      this.log('🔗 Combining results from all search engines...');
      const urlToContent = new Map<string, any>();

      const canonicalize = (url: string) => {
        try {
          const u = new URL(url);
          u.hash = '';
          const params = u.searchParams;
          const drop = [
            'utm_source','utm_medium','utm_campaign','utm_term','utm_content',
            'gclid','fbclid','ref','ref_src','igshid','mc_cid','mc_eid','aff','affiliate'
          ];
          drop.forEach(k => params.delete(k));
          u.search = params.toString() ? `?${params.toString()}` : '';
          return u.toString().toLowerCase().replace(/\/$/, '');
        } catch {
          return (url || '').toLowerCase().replace(/\/$/, '');
        }
      };

      // Add Firecrawl results (already have content)
      for (const result of firecrawlSearchResults) {
        const normalizedUrl = canonicalize(result.url);
        urlToContent.set(normalizedUrl, result);
        candidatesGross.firecrawl.add(normalizedUrl);
        ensureEntry(result.url).sources.add('firecrawl');
      }

      // Add Exa & Brave results (URLs only, need to scrape)
      const exaOnlyUrls: string[] = [];
      const braveOnlyUrls: string[] = [];
      for (const result of exaSearchResults) {
        const normalizedUrl = canonicalize(result.url);
        candidatesGross.exa.add(normalizedUrl);
        ensureEntry(result.url).sources.add('exa');
        if (!urlToContent.has(normalizedUrl)) {
          exaOnlyUrls.push(result.url);
        }
      }
      for (const result of braveSearchResults) {
        const normalizedUrl = canonicalize(result.url);
        candidatesGross.brave.add(normalizedUrl);
        ensureEntry(result.url).sources.add('brave');
        if (!urlToContent.has(normalizedUrl)) {
          braveOnlyUrls.push(result.url);
        }
      }

      const totalUrls = urlToContent.size + exaOnlyUrls.length + braveOnlyUrls.length;
      this.log(`✅ Combined ${totalUrls} unique URLs (${urlToContent.size} from Firecrawl, ${exaOnlyUrls.length} Exa-only, ${braveOnlyUrls.length} Brave-only)`);
      // Attribution (gross counts before filtering/whitelist)
      this.log(`📈 Attribution (gross) — Firecrawl: ${candidatesGross.firecrawl.size}, Exa: ${candidatesGross.exa.size}, Brave: ${candidatesGross.brave.size}`);

      // Show all URLs found by domain
      console.log(`\n📋 ALL URLS FOUND (DUAL SEARCH):`);
      let idx = 1;
      for (const [url, result] of urlToContent) {
        try {
          const domain = new URL(result.url).hostname;
          console.log(`  ${idx++}. ${domain} → ${result.url} [Firecrawl ✓]`);
        } catch (e) {
          console.log(`  ${idx++}. [Invalid URL] → ${result.url} [Firecrawl ✓]`);
        }
      }
      for (const url of exaOnlyUrls) {
        try {
          const domain = new URL(url).hostname;
          console.log(`  ${idx++}. ${domain} → ${url} [Exa]`);
        } catch (e) {
          console.log(`  ${idx++}. [Invalid URL] → ${url} [Exa]`);
        }
      }
      for (const url of braveOnlyUrls) {
        try {
          const domain = new URL(url).hostname;
          console.log(`  ${idx++}. ${domain} → ${url} [Brave]`);
        } catch (e) {
          console.log(`  ${idx++}. [Invalid URL] → ${url} [Brave]`);
        }
      }
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);

      // Step 2d: Visibility queries (no -site filters) using Exa + Brave only
      this.log('📊 Calculating search visibility quality scores...');
      const visibilityQueriesAll = QueryGenerator.getVisibilityQueries(content);
      const isAr = (q: string) => /[\u0600-\u06FF]/.test(q);
      const arVisAll = visibilityQueriesAll.filter(isAr);
      const enVisAll = visibilityQueriesAll.filter(q => !isAr(q));
      const visibilityQueries = [...enVisAll.slice(0,2), ...arVisAll.slice(0,2)];
      this.log(`   (Visibility queries used) EN: ${JSON.stringify(enVisAll.slice(0,2))}`);
      this.log(`   (Visibility queries used) AR: ${JSON.stringify(arVisAll.slice(0,2))}`);
      const sanitize = (q: string) => q.replace(/[“”]/g, '"').trim();

      const visibilityDataArray: VisibilityData[] = [];
      try { this.config.onProgressWithCount?.('Visibility scoring', 0, visibilityQueries.length); } catch {}
      for (const rawQ of visibilityQueries) {
        await this.waitIfPaused();
        const q = sanitize(rawQ).replace(/-site:\S+/g, '').trim();

        // EXA per‑query
        let exaRes: SearchResult[] = [];
        const exaAttempt = await retryWithBackoff<SearchResult[]>(
          () => this.exaCircuitBreaker.execute(() =>
            this.config.convexClient.action(api.exa.search, {
              query: q,
              numResults: this.config.maxResultsPerQuery ?? 10,
            })
          ),
          { maxAttempts: 3, initialDelayMs: 2000,
            onRetry: (attempt, error, delayMs) => {
              const categorized = categorizeError(error);
              this.log(`⚠️ Exa (visibility) failed for "${q}" (attempt ${attempt}): ${categorized.userMessage}`);
              this.log(`   Retrying in ${(delayMs / 1000).toFixed(1)}s...`);
            },
          }
        );
        if (exaAttempt.success && Array.isArray(exaAttempt.data)) {
          exaRes = (exaAttempt.data as any[]).map(r => ({ ...r, source: 'exa' as const }));
        }
        if (!exaAttempt.success) {
          console.warn(`[Visibility] Exa returned no results for: ${q}`);
        }

        // FIRECRAWL per-query
        let firecrawlRes: SearchResult[] = [];
        const firecrawlAttempt = await retryWithBackoff<any[]>(
          () => this.firecrawlCircuitBreaker.execute(() => this.firecrawlClient.search(q, this.config.maxResultsPerQuery ?? 10)),
          { maxAttempts: 2, initialDelayMs: 1000 }
        );
        if (firecrawlAttempt.success && firecrawlAttempt.data) {
          // Convert Firecrawl results to SearchResult format
          firecrawlRes = (firecrawlAttempt.data as any[]).map((item: any, idx: number): SearchResult => {
            let domain = '';
            try {
              domain = new URL(item.url).hostname;
            } catch {
              domain = item.url;
            }
            return {
              url: item.url,
              title: item.title || '',
              snippet: item.description || item.snippet || '',
              domain: domain,
              rank: idx + 1,
              source: 'firecrawl' as const,
            };
          });
        }

        // BRAVE per‑query with fallbacks
        const isArabicQ = /[\u0600-\u06FF]/.test(q);
        let braveRes: SearchResult[] = [];
        if (isArabicQ) {
          // Primary: SA
          const pri = await retryWithBackoff<SearchResult[]>(
            () => this.braveCircuitBreaker.execute(() => this.braveClient.searchMultiple([q], { numResults: this.config.maxResultsPerQuery ?? 10, lang: 'ar', country: 'sa', safesearch: 'off' })),
            { maxAttempts: 2, initialDelayMs: 1000 }
          );
          if (pri.success && pri.data) braveRes = pri.data as any;
          if (!pri.success || (braveRes?.length ?? 0) === 0) {
            for (const c of ['AE','EG','MA']) {
              const alt = await retryWithBackoff<SearchResult[]>(
                () => this.braveCircuitBreaker.execute(() => this.braveClient.searchMultiple([q], { numResults: this.config.maxResultsPerQuery ?? 10, lang: 'ar', country: c, safesearch: 'off' })),
                { maxAttempts: 2, initialDelayMs: 1000 }
              );
              if (alt.success && alt.data && (alt.data as any[]).length) { braveRes = alt.data as any; break; }
            }
            if ((braveRes?.length ?? 0) === 0) {
              const broad = await retryWithBackoff<SearchResult[]>(
                () => this.braveCircuitBreaker.execute(() => this.braveClient.searchMultiple([q], { numResults: this.config.maxResultsPerQuery ?? 10, safesearch: 'off' })),
                { maxAttempts: 2, initialDelayMs: 1000 }
              );
              if (broad.success && broad.data) braveRes = broad.data as any;
            }
          }
        } else {
          const pri = await retryWithBackoff<SearchResult[]>(
            () => this.braveCircuitBreaker.execute(() => this.braveClient.searchMultiple([q], { numResults: this.config.maxResultsPerQuery ?? 10, lang: 'en', safesearch: 'off' })),
            { maxAttempts: 2, initialDelayMs: 1000 }
          );
          if (pri.success && pri.data) braveRes = pri.data as any;
          if (!pri.success || (braveRes?.length ?? 0) === 0) {
            const broad = await retryWithBackoff<SearchResult[]>(
              () => this.braveCircuitBreaker.execute(() => this.braveClient.searchMultiple([q], { numResults: this.config.maxResultsPerQuery ?? 10, safesearch: 'off' })),
              { maxAttempts: 2, initialDelayMs: 1000 }
            );
            if (broad.success && broad.data) braveRes = broad.data as any;
          }
        }

        const combined = [...(exaRes || []), ...(firecrawlRes || []), ...(braveRes || [])];
        const vd = this.calculateVisibilityForQuery(q, combined, [content.title, ...(content as any).aliases || []], 'Exa + Firecrawl + Brave');
        visibilityDataArray.push(vd);
        try { this.config.onProgressWithCount?.('Visibility scoring', visibilityDataArray.length, visibilityQueries.length); } catch {}
      }

      // Calculate aggregate visibility score
      const avgVisibilityScore = visibilityDataArray.length > 0
        ? visibilityDataArray.reduce((sum, v) => sum + v.visibilityScore, 0) / visibilityDataArray.length
        : 0;

      if (visibilityDataArray.length > 0) {
        const bestScore = Math.max(...visibilityDataArray.map(v => v.visibilityScore));
        const worstScore = Math.min(...visibilityDataArray.map(v => v.visibilityScore));
        const avgOsnRank = visibilityDataArray
          .filter(v => v.osnRank !== null)
          .reduce((sum, v) => sum + v.osnRank!, 0) / visibilityDataArray.filter(v => v.osnRank !== null).length || null;

        this.log(`✅ Visibility Score: ${avgVisibilityScore.toFixed(1)}/100 (Best: ${bestScore.toFixed(1)}, Worst: ${worstScore.toFixed(1)})`);
        if (avgOsnRank) {
          this.log(`   OSN+ Average Rank: #${Math.round(avgOsnRank)}`);
        }
        console.log(`\n📈 SEARCH VISIBILITY ANALYSIS:`);
        visibilityDataArray.forEach((vd, i) => {
          console.log(`  Query ${i + 1}: "${vd.query.substring(0, 50)}${vd.query.length > 50 ? '...' : ''}"`);
          console.log(`    Score: ${vd.visibilityScore.toFixed(1)}/100 - ${vd.interpretation}`);
          console.log(`    OSN+ Rank: ${vd.osnRank ? `#${vd.osnRank}` : 'Not found'}, Pirates before OSN+: ${vd.piratesBeforeOsn}, Total pirates: ${vd.totalPirates}`);

          // Log engine/source provenance for debugging
          if (vd.osnUrls.length > 0) {
            const osnSources = vd.osnUrls.map(u => `#${u.rank} (${u.source || '?'})`).join(', ');
            console.log(`    OSN+ sources: ${osnSources}`);
          }
          if (vd.pirateUrls.length > 0) {
            const pirateSources = vd.pirateUrls.slice(0, 5).map(u => `#${u.rank} ${u.domain} (${u.source || '?'})`).join(', ');
            const moreCount = vd.pirateUrls.length > 5 ? ` +${vd.pirateUrls.length - 5} more` : '';
            console.log(`    Pirate sources: ${pirateSources}${moreCount}`);
          }
        });
        console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);
      } else {
        this.log('⚠️ No visibility data available (no search results)');
      }

      // Step 3: Filter out whitelisted domains
      this.log('🔒 Filtering out legitimate streaming services...');
      const whitelistedDomains: string[] = [];

      // Filter Firecrawl results
      const firecrawlCandidates: any[] = [];
      for (const [normalizedUrl, result] of urlToContent) {
        try {
          const domain = new URL(result.url).hostname;
          if (this.isWhitelisted(domain)) {
            whitelistedDomains.push(domain);
          } else {
            firecrawlCandidates.push(result);
            candidatesNet.firecrawl.add(normalizedUrl);
          }
        } catch (e) {
          this.log(`⚠️ Skipping malformed URL from Firecrawl: ${result.url}`);
          console.warn(`[Scanner] Failed to parse Firecrawl URL:`, e);
        }
      }

      // Filter Exa/Brave-only URLs
      const pendingScrapeUrls: string[] = [];
      const pendingScrapeSet = new Set<string>(); // Deduplicate URLs found by multiple engines
      const unsupportedDomains = [
        'pinterest.com','www.pinterest.com','jp.pinterest.com',
        'reddit.com','www.reddit.com',
        'twitter.com','x.com','www.twitter.com','www.x.com',
        'facebook.com','www.facebook.com','instagram.com','www.instagram.com',
        'tiktok.com','www.tiktok.com',
      ];
      const isUnsupported = (host: string) => unsupportedDomains.some(d => host.endsWith(d));
      for (const url of exaOnlyUrls) {
        try {
          const domain = new URL(url).hostname;
          if (this.isWhitelisted(domain)) {
            whitelistedDomains.push(domain);
          } else {
            if (isUnsupported(domain)) {
              this.log(`⏭ Skipping scrape (unsupported): ${domain}`);
            } else {
              const canonUrl = canonicalize(url);
              if (!pendingScrapeSet.has(canonUrl)) {
                pendingScrapeSet.add(canonUrl);
                pendingScrapeUrls.push(url);
              }
            }
            candidatesNet.exa.add(canonicalize(url));
          }
        } catch (e) {
          this.log(`⚠️ Skipping malformed URL from Exa: ${url}`);
          console.warn(`[Scanner] Failed to parse Exa URL:`, e);
        }
      }
      for (const url of braveOnlyUrls) {
        try {
          const domain = new URL(url).hostname;
          if (this.isWhitelisted(domain)) {
            whitelistedDomains.push(domain);
          } else {
            if (isUnsupported(domain)) {
              this.log(`⏭ Skipping scrape (unsupported): ${domain}`);
            } else {
              const canonUrl = canonicalize(url);
              if (!pendingScrapeSet.has(canonUrl)) {
                pendingScrapeSet.add(canonUrl);
                pendingScrapeUrls.push(url);
              }
            }
            candidatesNet.brave.add(canonicalize(url));
          }
        } catch (e) {
          this.log(`⚠️ Skipping malformed URL from Brave: ${url}`);
          console.warn(`[Scanner] Failed to parse Brave URL:`, e);
        }
      }

      const totalCandidates = firecrawlCandidates.length + pendingScrapeUrls.length;
      const totalFiltered = whitelistedDomains.length;
      this.log(`✅ ${totalCandidates} URLs to analyze (${totalFiltered} filtered as legitimate)`);
      // Attribution (net after whitelist/unsupported filtering)
      this.log(`📊 Attribution (net) — Firecrawl: ${candidatesNet.firecrawl.size}, Exa: ${candidatesNet.exa.size}, Brave: ${candidatesNet.brave.size}`);

      if (whitelistedDomains.length > 0) {
        console.log(`\n🔒 WHITELISTED DOMAINS (legitimate services):`);
        [...new Set(whitelistedDomains)].forEach(domain => console.log(`  • ${domain}`));
        console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);
      }

      if (totalCandidates === 0) {
        this.log('⚠️ No candidate URLs to scan - all were legitimate services');
        return { violations: [], visibilityData: visibilityDataArray };
      }

      // Step 4: Scrape Exa-only URLs (Firecrawl search already has content)
      this.log(`📊 Content ready: ${firecrawlCandidates.length} (from Firecrawl search)`);
      this.log(`🌐 Need to scrape: ${pendingScrapeUrls.length} (from Exa/Brave search)`);

      const crawlResults: any[] = [...firecrawlCandidates];

      if (pendingScrapeUrls.length > 0) {
        this.logWithCount('Scraping remaining URLs...', 0, pendingScrapeUrls.length);
        this.log('⏳ This may take 30-60 seconds...');

        // Prepare origin map to attribute scraped successes
        const urlOrigin: Record<string, Set<'exa'|'brave'>> = {};
        for (const u of exaOnlyUrls) {
          const key = canonicalize(u);
          urlOrigin[key] = urlOrigin[key] || new Set();
          urlOrigin[key].add('exa');
        }
        for (const u of braveOnlyUrls) {
          const key = canonicalize(u);
          urlOrigin[key] = urlOrigin[key] || new Set();
          urlOrigin[key].add('brave');
        }

        // Process in batches to show progress
        const batchSize = 10;

        for (let i = 0; i < pendingScrapeUrls.length; i += batchSize) {
          await this.waitIfPaused();
          const batch = pendingScrapeUrls.slice(i, i + batchSize);

          const scrapeResult = await retryWithBackoff<any[]>(
            () => this.firecrawlCircuitBreaker.execute(() =>
              this.firecrawlClient.scrapeMultiple(batch, { concurrency: 5 })
            ),
            {
              maxAttempts: 2,
              initialDelayMs: 3000,
              onRetry: (attempt, error, delayMs) => {
                const categorized = categorizeError(error);
                this.log(`⚠️ Scraping batch failed (attempt ${attempt}): ${categorized.userMessage}`);
                this.log(`   Retrying in ${(delayMs / 1000).toFixed(1)}s...`);
              },
            }
          );

          if (scrapeResult.success && scrapeResult.data) {
            crawlResults.push(...scrapeResult.data);
            // Mark scraped success and increment per-engine counters
            for (const r of scrapeResult.data) {
              const key = canonicalize(r.url);
              const entry = attributionMap.get(key) || { sources: new Set<'exa'|'firecrawl'|'brave'>() };
              attributionMap.set(key, entry);
              entry.scraped = true;
              const origins = urlOrigin[key] || new Set();
              if (origins.has('exa')) scrapedSuccess.exa++;
              if (origins.has('brave')) scrapedSuccess.brave++;
            }
          } else {
            this.log(`❌ Failed to scrape batch after retries, continuing...`);
          }

          const processed = Math.min(i + batchSize, pendingScrapeUrls.length);
          const sampleDomains = batch
            .slice(0, 3)
            .map((u) => {
              try { return new URL(u).hostname.replace(/^www\./,''); } catch { return u; }
            })
            .join(', ');
          this.logWithCount(`Scraping: ${sampleDomains}`, processed, pendingScrapeUrls.length);
        }

        const scrapedCount = crawlResults.length - firecrawlCandidates.length;
        this.log(`✅ Successfully scraped ${scrapedCount}/${pendingScrapeUrls.length} URLs (Exa/Brave)`);
        this.log(`🔍 Scrape success attribution — Exa-origin: ${scrapedSuccess.exa}, Brave-origin: ${scrapedSuccess.brave}`);

        if (scrapedCount < pendingScrapeUrls.length) {
          this.log(`⚠️ ${pendingScrapeUrls.length - scrapedCount} pages failed to scrape (timeout/error)`);

          // Show which URLs failed to crawl
          const successfulUrls = new Set(crawlResults.map(r => r.url?.toLowerCase().replace(/\/$/, '')));
          const failedUrls = pendingScrapeUrls.filter(url => !successfulUrls.has(url.toLowerCase().replace(/\/$/, '')));

          console.log(`\n❌ FAILED TO SCRAPE (timeout/blocked/error):`);
          failedUrls.forEach(url => {
            try {
              const domain = new URL(url).hostname;
              console.log(`  • ${domain} → ${url}`);
            } catch (e) {
              console.log(`  • [Invalid URL] → ${url}`);
            }
          });
          console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);
        }
      }

      // Store failed URLs for fallback classification
      const successfulUrls = new Set(crawlResults.map(r => r.url ? canonicalize(r.url) : ''));
      const allAttemptedUrls = [...firecrawlCandidates.map(r => r.url), ...pendingScrapeUrls];
      const failedUrls = allAttemptedUrls.filter(url => !successfulUrls.has(canonicalize(url)));

      this.log(`✅ Total pages ready for analysis: ${crawlResults.length}`);

      // Step 5: Classify pages with LLM
      this.log('🤖 Using AI to validate pages for piracy...');
      const violations: any[] = [];
      const classifyBatchSize = 15;
      try { this.config.onProgressWithCount?.('Classifying', 0, crawlResults.length); } catch {}
      for (let i = 0; i < crawlResults.length; i += classifyBatchSize) {
        await this.waitIfPaused();
        const batch = crawlResults.slice(i, i + classifyBatchSize);
        const llmResult = await retryWithBackoff<any[]>(
          () => this.llmCircuitBreaker.execute(() =>
            Classifier.classifyMultipleWithLLM(
              batch,
              content.title,
              this.openRouterClient
            )
          ),
          {
            maxAttempts: 3,
            initialDelayMs: 5000,
            maxDelayMs: 30000,
            onRetry: (attempt, error, delayMs) => {
              const categorized = categorizeError(error);
              this.log(`⚠️ LLM classification failed (attempt ${attempt}): ${categorized.userMessage}`);
              this.log(`   Retrying in ${(delayMs / 1000).toFixed(1)}s...`);
            },
          }
        );
        if (llmResult.success && Array.isArray(llmResult.data)) {
          violations.push(...llmResult.data);
        } else if (!llmResult.success) {
          this.log(`❌ LLM classification failed after retries: ${llmResult.error?.message}`);
        }
        const done = Math.min(i + batch.length, crawlResults.length);
        try { this.config.onProgressWithCount?.('Classifying', done, crawlResults.length); } catch {}
      }
      if (violations.length === 0) {
        this.log('⚠️ Falling back to search result classification only...');
      }
      this.log(`✅ Detected ${violations.length} verified violations from crawled pages`);

      // Step 5b: Fallback classification for unreachable sites
      if (failedUrls.length > 0) {
        this.log(`🔍 Applying fallback classification to ${failedUrls.length} unreachable sites...`);

        // Get search result metadata for failed URLs
        const allSearchResults = [...exaSearchResults, ...braveSearchResults, ...firecrawlSearchResults];

        for (const failedUrl of failedUrls) {
          await this.waitIfPaused();
          const normalizedUrl = failedUrl.toLowerCase().replace(/\/$/, '');
          const searchResult = allSearchResults.find(r =>
            canonicalize(r.url) === normalizedUrl
          );

          if (searchResult) {
            // Only apply fallback if domain is known piracy host; otherwise skip
            const host = (() => { try { return new URL(searchResult.url).hostname; } catch { return ''; } })();
            if (Classifier.isKnownPiracyHost(host)) {
              const fallbackViolation = Classifier.classifyFromSearchResult(
                searchResult,
                [content.title, ...(content as any).aliases || []]
              );
              if (fallbackViolation) {
                violations.push(fallbackViolation);
              }
            } else {
              this.log(`⏭ Skipping unreachable non-piracy host: ${host}`);
            }
          }
        }

        const unverifiedCount = violations.filter(v => v.verified === false).length;
        const verifiedCount = violations.filter(v => v.verified === true).length;

        this.log(`✅ Total violations: ${violations.length} (${verifiedCount} verified, ${unverifiedCount} unverified)`);
      } else {
        violations.forEach((v, i) => {
          this.log(`  Violation ${i+1}: ${v.domain} (${v.score}% confidence)`);
        });
      }

      // Summarize violation attribution by engine
      try {
        const canon = (u: string) => {
          try { const x = new URL(u); x.hash = ''; return x.toString().toLowerCase().replace(/\/$/, ''); } catch { return (u||'').toLowerCase().replace(/\/$/, ''); }
        };
        const counts = { exa: 0, firecrawl: 0, brave: 0 } as Record<'exa'|'firecrawl'|'brave', number>;
        for (const v of violations) {
          const key = canon(v.url);
          const entry = attributionMap.get(key);
          if (entry) {
            if (entry.sources.has('exa')) counts.exa++;
            if (entry.sources.has('firecrawl')) counts.firecrawl++;
            if (entry.sources.has('brave')) counts.brave++;
          }
        }
        this.log(`📌 Violation attribution — Firecrawl: ${counts.firecrawl}, Exa: ${counts.exa}, Brave: ${counts.brave}`);
      } catch {}

      this.log(`🎉 Scan complete: ${violations.length} violations found for "${content.title}"`);
      return { violations, visibilityData: visibilityDataArray };

    } catch (error: any) {
      this.log(`❌ Scan error: ${error.message}`);
      console.error(`Scan error for ${content.title}:`, error);
      throw new Error(`Scan failed: ${error.message}`);
    }
  }

  /**
   * Scan multiple content titles
   */
  async scanMultiple(contents: ContentInput[]): Promise<Map<string, { violations: Violation[]; visibilityData: VisibilityData[] }>> {
    const results = new Map<string, { violations: Violation[]; visibilityData: VisibilityData[] }>();

    for (const content of contents) {
      try {
        const scanResult = await this.scanContent(content);
        results.set(content.title, scanResult);

        // Small delay between scans to respect rate limits
        await this.delay(2000);
      } catch (error: any) {
        console.error(`Failed to scan ${content.title}:`, error);
        results.set(content.title, { violations: [], visibilityData: [] });
      }
    }

    return results;
  }

  /**
   * Quick scan - limited queries, no screenshots
   */
  async quickScan(content: ContentInput): Promise<Violation[]> {
    const queries = QueryGenerator.generateQueries(content).slice(0, 3);

    const searchResults = await this.config.convexClient.action(api.exa.searchMultiple, {
      queries,
      numResults: 5,
    });

    const candidateUrls = searchResults
      .filter(result => !Whitelist.isWhitelisted(result.domain))
      .map(result => result.url)
      .slice(0, 10);

    if (candidateUrls.length === 0) {
      return [];
    }

    const crawlResults = await this.firecrawlClient.scrapeMultiple(
      candidateUrls,
      { concurrency: 5 }
    );

    return Classifier.classifyMultiple(crawlResults, content.title);
  }

  /**
   * Deep scan - extensive queries
   */
  async deepScan(content: ContentInput): Promise<Violation[]> {
    const originalMaxResults = this.config.maxResultsPerQuery;

    // Temporarily increase limits for deep scan
    this.config.maxResultsPerQuery = 15;

    try {
      const result = await this.scanContent(content);
      return result.violations;
    } finally {
      // Restore original settings
      this.config.maxResultsPerQuery = originalMaxResults;
    }
  }

  /**
   * Rescan specific URLs (for follow-up checks)
   */
  async rescanUrls(urls: string[], contentTitle: string): Promise<Violation[]> {
    this.log(`Rescanning ${urls.length} URLs for ${contentTitle}`);

    const crawlResults = await this.firecrawlClient.scrapeMultiple(urls);
    const violations = Classifier.classifyMultiple(crawlResults, contentTitle);

    return violations;
  }

  /**
   * Verify if a specific URL is still a violation
   */
  async verifyViolation(url: string, contentTitle: string): Promise<boolean> {
    const crawlResult = await this.firecrawlClient.scrape(url);
    if (!crawlResult) {
      return false;
    }

    const violation = Classifier.classifyPage(crawlResult, contentTitle);
    return violation !== null;
  }

  /**
   * Calculate search visibility scores for all queries
   */
  private calculateVisibilityForQuery(query: string, results: SearchResult[], contentTitles: string[], searchEngineLabel?: string): VisibilityData {
    // Canonicalize and deduplicate by URL, keeping best rank
    const canonicalize = (url: string) => {
      try {
        const u = new URL(url);
        u.hash = '';
        const params = u.searchParams;

        // Domain-specific whitelist: parameters that identify distinct content
        const meaningfulParams: Record<string, string[]> = {
          // Video platforms
          'youtube.com': ['v', 'list', 'feature', 'shorts'], // video ID, playlist, feature forms, shorts
          'youtu.be': ['v', 'feature'], // Note: youtu.be typically uses path-based IDs, not query params
          'dailymotion.com': ['video'],
          'vimeo.com': ['v', 'video'],
          'twitch.tv': ['video', 'channel'],
          'tiktok.com': ['v', 'video'],

          // Social media
          'reddit.com': ['p', 'comment', 'comments'],
          'twitter.com': ['status'],
          'x.com': ['status'],
          'instagram.com': ['p'],

          // Blogging platforms
          'medium.com': ['p', 'post'],
          'wordpress.com': ['p', 'post'],
          'tumblr.com': ['post'],

          // News sites (major outlets)
          'cnn.com': ['article', 'id'],
          'bbc.com': ['article', 'id'],
          'bbc.co.uk': ['article', 'id'],
          'nytimes.com': ['article', 'id'],
          'theguardian.com': ['article', 'id'],
          'washingtonpost.com': ['article', 'id'],
          'reuters.com': ['article', 'id'],
          'apnews.com': ['article', 'id'],
          'aljazeera.com': ['article', 'id'],
          'alarabiya.net': ['article', 'id'],

          // Streaming/piracy sites (common patterns)
          // Keep content identifiers to avoid false deduplication
        };

        // Get whitelist for this domain
        const hostname = u.hostname.toLowerCase().replace(/^www\./, '');
        const keepParams = meaningfulParams[hostname] || [];

        // Tracking/analytics params to always drop
        const drop = [
          'utm_source','utm_medium','utm_campaign','utm_term','utm_content',
          'gclid','fbclid','ref','ref_src','igshid','mc_cid','mc_eid','aff','affiliate',
          'source','share','from','via'
        ];

        // Remove all drop params
        drop.forEach(k => params.delete(k));

        // Remove all other params except whitelisted ones
        const allParams = Array.from(params.keys());
        for (const key of allParams) {
          if (!keepParams.includes(key)) {
            // Don't drop params that are in the whitelist
            // For non-whitelisted domains, keep common content-identifying params
            const commonContentParams = ['id', 'p', 'page', 'article', 'post', 'v', 'video'];
            if (!commonContentParams.includes(key.toLowerCase())) {
              params.delete(key);
            }
          }
        }

        u.search = params.toString() ? `?${params.toString()}` : '';
        return u.toString().toLowerCase().replace(/\/$/, '');
      } catch {
        return (url || '').toLowerCase().replace(/\/$/, '');
      }
    };

    const byUrl = new Map<string, SearchResult>();
    for (const r of results) {
      const key = canonicalize(r.url);
      const existing = byUrl.get(key);
      // FIX: Treat rank 0 (Brave placeholder) as lowest priority during deduplication
      // This matches the sorting logic below and prevents Brave's rank 0 from
      // incorrectly overriding valid ranks from Exa/Firecrawl
      const rankCurrent = r.rank ?? 9999;
      const rankExisting = existing?.rank ?? 9999;
      const effectiveRankCurrent = rankCurrent === 0 ? 9999 : rankCurrent;
      const effectiveRankExisting = rankExisting === 0 ? 9999 : rankExisting;
      if (!existing || effectiveRankCurrent < effectiveRankExisting) {
        byUrl.set(key, r);
      }
    }
    let dedupedResults = Array.from(byUrl.values());

    // FIX: Assign global sequential ranks to avoid duplicates from multiple engines
    // Sort by original rank (treating 0 and undefined as lowest priority)
    // NOTE: Brave API returns rank=0 as placeholder; this logic treats it as lowest priority.
    // If Brave changes to return real ranks in the future, this code will still work correctly
    // (the rank=0 check will simply become a no-op).
    dedupedResults.sort((a, b) => {
      const rankA = a.rank ?? 9999;
      const rankB = b.rank ?? 9999;
      // Treat rank 0 (from Brave) as lowest priority
      const effectiveA = rankA === 0 ? 9999 : rankA;
      const effectiveB = rankB === 0 ? 9999 : rankB;

      // Primary sort: by effective rank
      if (effectiveA !== effectiveB) {
        return effectiveA - effectiveB;
      }

      // Stable tie-breaker: engine precedence (Exa > Firecrawl > Brave)
      // This ensures deterministic ordering when multiple engines return same rank
      const enginePriority = { exa: 1, firecrawl: 2, brave: 3 };
      const priorityA = enginePriority[a.source || 'brave'] || 9;
      const priorityB = enginePriority[b.source || 'brave'] || 9;
      return priorityA - priorityB;
    });

    // Assign new sequential ranks (1, 2, 3, ...)
    dedupedResults = dedupedResults.map((result, index) => ({
      ...result,
      rank: index + 1,
    }));

    // OPTIMIZATION: Build canonical URL map to avoid O(n²) lookups in classification callback
    // This precomputation reduces complexity from O(n²) to O(n) for the classification phase
    const canonicalMap = new Map<string, SearchResult>();
    for (const result of dedupedResults) {
      const key = canonicalize(result.url);
      canonicalMap.set(key, result);
    }

    // Classify results (OSN vs Pirate vs Other)
    // NOTE: This uses proper piracy detection (keywords, URL patterns, etc.)
    // and does NOT classify domains as pirate just because they're non-OSN.
    // Only results with actual piracy signals will be counted as pirates.
    const classified = this.visibilityScorer.classifyResults(
      dedupedResults,
      (domain: string, url: string) => {
        if (this.isWhitelisted(domain)) return false;
        // O(1) lookup instead of O(n) find
        const sr = canonicalMap.get(canonicalize(url));
        if (!sr || sr.domain !== domain) return false;
        const result = Classifier.classifyFromSearchResult(
          {
            url: sr.url,
            title: sr.title || '',
            snippet: sr.snippet || '',
            domain: sr.domain,
          },
          contentTitles,
          'visibility'
        );
        return result !== null;
      }
    );

    const visibilityScore = this.visibilityScorer.calculateScore(classified);

    return {
      query,
      searchEngine: searchEngineLabel || 'Exa + Firecrawl + Brave',
      visibilityScore: visibilityScore.score,
      osnRank: visibilityScore.osnRank,
      piratesBeforeOsn: visibilityScore.piratesBeforeOsn,
      totalPirates: visibilityScore.totalPirates,
      totalResults: visibilityScore.totalResults,
      interpretation: visibilityScore.interpretation,
      alpha: visibilityScore.alpha,
      beta: visibilityScore.beta,
      osnUrls: classified.osnUrls,
      pirateUrls: classified.pirateUrls,
    };
  }

  /**
   * Check if domain is whitelisted
   */
  private isWhitelisted(domain: string): boolean {
    const normalized = domain.toLowerCase().replace(/^www\./, '');

    // Exact match
    if (this.whitelistDomains.has(normalized)) {
      console.log(`[Whitelist] ✓ Exact match: ${domain} → ${normalized}`);
      return true;
    }

    // Subdomain match (e.g., "play.google.com" matches "google.com")
    for (const whitelistedDomain of this.whitelistDomains) {
      if (normalized.endsWith(`.${whitelistedDomain}`) || normalized === whitelistedDomain) {
        console.log(`[Whitelist] ✓ Subdomain match: ${domain} → ${normalized} matches ${whitelistedDomain}`);
        return true;
      }
    }

    return false;
  }

  /**
   * Log progress messages
   */
  private log(message: string): void {
    if (this.config.onProgress) {
      this.config.onProgress(message);
    }
    console.log(`[PiracyScanner] ${message}`);
  }

  /**
   * Log progress with count
   */
  private logWithCount(message: string, current: number, total: number): void {
    if (this.config.onProgressWithCount) {
      this.config.onProgressWithCount(message, current, total);
    }
    console.log(`[PiracyScanner] ${message} (${current}/${total})`);
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async waitIfPaused(): Promise<void> {
    try {
      // @ts-ignore
      if (typeof window === 'undefined' || !window?.localStorage) return;
      // @ts-ignore
      while (window.localStorage.getItem('scanner:paused') === '1') {
        await this.delay(500);
      }
    } catch {}
  }

  /**
   * Convert a simple domain entry into a safe regex string.
   * - If it contains a dot (e.g., "egybest.com") -> match exact TLD + subdomains: /(^|\.)egybest\.com$/i
   * - If it is a label only (e.g., "egybest") -> match any TLD under that label: /(^|\.)egybest\./i
   */
  private compileDomainToRegex(entry: string): string {
    const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const raw = (entry || '').trim().toLowerCase();
    if (!raw) return '';
    if (raw.includes('/')) return ''; // not a domain
    // IDN/punycode is passed through as-is; regex works on ASCII dot labels
    if (raw.includes('.')) {
      // Full hostname (tld included)
      return `(^|\\.)${escape(raw)}$`;
    }
    // Label only -> any TLD
    return `(^|\\.)${escape(raw)}\\.`;
  }
}

/**
 * Utility function to deduplicate URLs
 */
export function deduplicateUrls(urls: string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];

  for (const url of urls) {
    const normalized = url.toLowerCase().replace(/\/$/, '');
    if (!seen.has(normalized)) {
      seen.add(normalized);
      unique.push(url);
    }
  }

  return unique;
}

/**
 * Utility function to extract domain from URL
 */
export function extractDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}
